/**
 * feAI – Frontend
 * src/App.tsx
 *
 * Root component: sets up React Router with public and protected routes.
 */

import React, { Suspense, lazy, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LoginPage }          from './pages/LoginPage';
import { SsoAuthPage }        from './pages/SsoAuthPage';
import { AgentiDashboardPage } from './pages/AgentiDashboardPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage }  from './pages/ResetPasswordPage';
import { AppShell }           from './components/layout/AppShell';
import { PageGuard }          from './components/permissions/PageGuard';
import { FirstAccessibleRedirect } from './components/layout/FirstAccessibleRedirect';
import { PageTrackingProvider } from './hooks/usePageTracking';

// Caricato in lazy — chunk separato, non incluso nel bundle principale
const BrandCatalogPage = lazy(() => import('./pages/BrandCatalogPage'));
const UsersPage        = lazy(() => import('./pages/UsersPage').then(m => ({ default: m.UsersPage })));
const ArchiviatiPage  = lazy(() => import('./pages/ArchiviatiPage').then(m => ({ default: m.ArchiviatiPage })));
const AdminDettaglioTabsPage = lazy(() => import('./pages/AdminDettaglioTabsPage').then(m => ({ default: m.AdminDettaglioTabsPage })));
const RetentionPage = lazy(() => import('./pages/RetentionPage').then(m => ({ default: m.RetentionPage })));
const PermissionsPage = lazy(() => import('./pages/PermissionsPage').then(m => ({ default: m.PermissionsPage })));
const PartiteApertePage = lazy(() => import('./pages/PartiteApertePage'));
const MailReportPage = lazy(() => import('./pages/MailReportPage'));
const ReportApparoundPage = lazy(() => import('./pages/ReportApparoundPage').then(m => ({ default: m.ReportApparoundPage })));
const ReportAccessiPage = lazy(() => import('./pages/ReportAccessiPage').then(m => ({ default: m.ReportAccessiPage })));
const PageAccessReportPage = lazy(() => import('./pages/PageAccessReportPage').then(m => ({ default: m.PageAccessReportPage })));
const GerarchiaAgentiPage = lazy(() => import('./pages/GerarchiaAgentiPage').then(m => ({ default: m.GerarchiaAgentiPage })));
// NOTA: Clienti, Lead, Vendite, Contratti, Agenti, Pratiche
// appartengono al progetto utilityos.futurenergyonline.it (OS),
// non ad AI. Per eventuali redirect futuri, vedi CatchAllRedirect.

/** Minimal auth guard: redirects to /login if no token is stored. */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hasToken = Boolean(localStorage.getItem('feai_access_token'));
  return hasToken ? <>{children}</> : <Navigate to="/login" replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,        // 30 sec prima di rifetch
      retry: 1,                 // 1 retry su fallimento
      refetchOnWindowFocus: false,
    },
  },
});

/** Wraps routes with AppShell and proper navigation. */
const AppShellRoute: React.FC<{ activePage: string; children: React.ReactNode }> = ({
  activePage,
  children,
}) => {
  const navigate = useNavigate();
  const onNavigate = useCallback(
    (page: string) => navigate(`/${page}`),
    [navigate]
  );
  return (
    <AppShell activePage={activePage} onNavigate={onNavigate}>
      {children}
    </AppShell>
  );
};

/**
 * Catch-all redirect che:
 *   - Preserva i parametri SSO (sso_token, sso_cod_agente, sso_username, sso_ts)
 *     reindirizzando a /sso-auth.
 *   - Se l'utente è già autenticato, lo reindirizza alla prima pagina
 *     del menu a cui ha accesso (invece che sempre a /dashboard).
 *   - Altrimenti redirect a /login.
 */
const CatchAllRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const ssoToken       = searchParams.get('sso_token');
  const ssoCodAgente   = searchParams.get('sso_cod_agente');
  const ssoUsername    = searchParams.get('sso_username');
  const ssoTs          = searchParams.get('sso_ts');

  // SSO — utente non ancora autenticato
  if (ssoToken && ssoCodAgente && ssoUsername && ssoTs) {
    const params = new URLSearchParams({
      sso_token: ssoToken,
      sso_cod_agente: ssoCodAgente,
      sso_username: ssoUsername,
      sso_ts: ssoTs,
    });
    return <Navigate to={`/sso-auth?${params.toString()}`} replace />;
  }

  // Utente già autenticato → prima pagina accessibile
  const hasToken = Boolean(localStorage.getItem('feai_access_token'));
  if (hasToken) {
    return <FirstAccessibleRedirect />;
  }

  return <Navigate to="/login" replace />;
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
  <BrowserRouter>
    <PageTrackingProvider />
    <Routes>
      {/* ── Auth ── */}
      <Route path="/sso-auth"        element={<SsoAuthPage />} />
      <Route path="/login"            element={<LoginPage />} />
      <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
      <Route path="/reset-password"   element={<ResetPasswordPage />} />

      {/* ── Pubblica (no auth) ── */}
      <Route path="/brand/catalog" element={
        <Suspense fallback={<div style={{background:'#1a1a1a',color:'#666',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>Caricamento catalogo…</div>}>
          <BrandCatalogPage />
        </Suspense>
      } />

      {/* ── Protetta ── */}
      {/* Dashboard unificata: la vecchia DashboardPage è stata rimossa perché
          ridondante con AgentiDashboardPage. Il titolo varia per ruolo
          (DashBoard Admin / DashBoard Inserter / DashBoard Agenti). */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="dashboard">
              <PageGuard pageId="dashboard">
                <AgentiDashboardPage />
              </PageGuard>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/agenti-dashboard"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="agenti-dashboard">
              <PageGuard pageId="agenti-dashboard">
                <AgentiDashboardPage />
              </PageGuard>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="users">
              <Suspense fallback={<div style={{padding: 40, textAlign: 'center'}}>Caricamento utenti…</div>}>
                <PageGuard pageId="users">
                  <UsersPage />
                </PageGuard>
              </Suspense>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/archiviati"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="archiviati">
              <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento archiviati…</div>}>
                <PageGuard pageId="archiviati">
                  <ArchiviatiPage />
                </PageGuard>
              </Suspense>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/archiviati/tabs"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="archiviati/tabs">
              <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento configurazione…</div>}>
                <PageGuard pageId="archiviati">
                  <AdminDettaglioTabsPage />
                </PageGuard>
              </Suspense>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/dettaglio-tabs"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="admin/dettaglio-tabs">
              <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento configurazione…</div>}>
                <PageGuard pageId="admin/dettaglio-tabs">
                  <AdminDettaglioTabsPage />
                </PageGuard>
              </Suspense>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/retention"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="retention">
              <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento retention clienti…</div>}>
                <PageGuard pageId="retention">
                  <RetentionPage />
                </PageGuard>
              </Suspense>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/permissions"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="admin/permissions">
              <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento permessi…</div>}>
                <PageGuard pageId="admin/permissions">
                  <PermissionsPage />
                </PageGuard>
              </Suspense>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />
      {/* Route OS (utilityos) rimosse — non appartengono ad AI */}

      <Route
        path="/partite-aperte"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="partite-aperte">
              <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento partite aperte…</div>}>
                <PageGuard pageId="partite-aperte">
                  <PartiteApertePage />
                </PageGuard>
              </Suspense>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/mail-report"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="mail-report">
              <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento mail report…</div>}>
                <PageGuard pageId="mail-report">
                  <MailReportPage />
                </PageGuard>
              </Suspense>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      {/* ── Report Interni (iframe srcdoc con autenticazione JWT) ── */}
      <Route
        path="/report-apparound"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="report-apparound">
              <PageGuard pageId="report-apparound">
                <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento report Apparound…</div>}>
                  <ReportApparoundPage />
                </Suspense>
              </PageGuard>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/report-accessi"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="report-accessi">
              <PageGuard pageId="report-accessi">
                <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento report accessi…</div>}>
                  <ReportAccessiPage />
                </Suspense>
              </PageGuard>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/page-access-report"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="page-access-report">
              <PageGuard pageId="report-accessi">
                <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento report navigazione…</div>}>
                  <PageAccessReportPage />
                </Suspense>
              </PageGuard>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gerarchia-agenti"
        element={
          <ProtectedRoute>
            <AppShellRoute activePage="gerarchia-agenti">
              <PageGuard pageId="gerarchia-agenti">
                <Suspense fallback={<div style={{padding: 40, textAlign: "center"}}>Caricamento gerarchia agenti…</div>}>
                  <GerarchiaAgentiPage />
                </Suspense>
              </PageGuard>
            </AppShellRoute>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  </BrowserRouter>
  </QueryClientProvider>
);

export default App;
